/**
 * EditRageRoom
 * ------------
 * Dedicated admin surface for updating an existing rage room.
 * Prefers navigation state for instant hydration, but also refreshes data
 * from the Rage Room API and persists changes via admin.updateRoom.
 */
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import useApi from '../hooks/useApi';

const buildFormState = (room = {}) => ({
	name: room.name || '',
	city: room.city || '',
	locationName: room.locationName || '',
	locationAddress: room.locationAddress || '',
	description: room.description || '',
	capacity: room.capacity ?? 1,
	amenities: Array.isArray(room.amenities) ? room.amenities.join(', ') : '',
	isActive: room.isActive !== false
});

const parseAmenities = (value = '') => value
	.split(',')
	.map((item) => item.trim())
	.filter(Boolean);

const EditRageRoom = () => {
	const { roomId } = useParams();
	const navigate = useNavigate();
	const location = useLocation();
	const { rageRoom } = useApi();

	const locationId = process.env.REACT_APP_DEFAULT_LOCATION_ID || 'default-location';
	const roomFromState = location.state?.room;

	const [formValues, setFormValues] = useState(() => buildFormState(roomFromState || {}));
	const [loading, setLoading] = useState(!roomFromState);
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState(null);
	const [successMessage, setSuccessMessage] = useState('');
	const [roomLabel, setRoomLabel] = useState(roomFromState?.name || '');

	useEffect(() => {
		if (!rageRoom?.admin?.getRoom || !roomId) {
			setLoading(false);
			return;
		}

		let isMounted = true;

		const fetchRoom = async () => {
			setLoading(true);
			setError(null);
			try {
				const response = await rageRoom.admin.getRoom({ locationId, roomId });
				if (!isMounted) {
					return;
				}
				const resolvedRoom = response?.room || response?.data?.room || response?.data || response;
				if (resolvedRoom) {
					setRoomLabel(resolvedRoom.name || '');
					setFormValues(buildFormState(resolvedRoom));
				}
			} catch (fetchError) {
				if (isMounted) {
					setError(fetchError.message || 'Unable to load rage room details.');
				}
			} finally {
				if (isMounted) {
					setLoading(false);
				}
			}
		};

		fetchRoom();

		return () => {
			isMounted = false;
		};
	}, [locationId, roomId]);

	const handleFieldChange = (field) => (event) => {
		const value = event.target.value;
		setFormValues((prev) => ({
			...prev,
			[field]: field === 'capacity' ? value.replace(/[^0-9]/g, '') : value
		}));
	};

	const handleIsActiveChange = (event) => {
		const { checked } = event.target;
		setFormValues((prev) => ({
			...prev,
			isActive: checked
		}));
	};

	const isSubmitDisabled = useMemo(() => {
		return submitting
			|| loading
			|| !formValues.name.trim()
			|| !formValues.city.trim()
			|| !formValues.locationName.trim()
			|| !formValues.locationAddress.trim();
	}, [formValues.city, formValues.locationAddress, formValues.locationName, formValues.name, loading, submitting]);

	const handleSubmit = async (event) => {
		event.preventDefault();

		if (!rageRoom?.admin?.updateRoom || !roomId) {
			setError('Update endpoint is unavailable.');
			return;
		}

		setSubmitting(true);
		setError(null);
		setSuccessMessage('');

		try {
			const payload = {
				name: formValues.name.trim(),
				city: formValues.city.trim(),
				locationName: formValues.locationName.trim(),
				locationAddress: formValues.locationAddress.trim(),
				description: formValues.description.trim(),
				capacity: Math.max(1, Number(formValues.capacity) || 1),
				isActive: formValues.isActive,
				amenities: parseAmenities(formValues.amenities)
			};

			await rageRoom.admin.updateRoom({
				locationId,
				roomId,
				payload
			});

			setSuccessMessage('Rage room updated successfully.');
		} catch (submitError) {
			setError(submitError.message || 'Unable to update rage room.');
		} finally {
			setSubmitting(false);
		}
	};

	const handleCancel = () => {
		navigate('/admin/rooms');
	};

	return (
		<Stack spacing={4}>
			<Box>
				<Button variant="text" color="primary" onClick={() => navigate(-1)} sx={{ mb: 2 }}>
					Back
				</Button>
				<Typography variant="h4" gutterBottom>
					{roomLabel ? `Edit ${roomLabel}` : 'Edit Rage Room'}
				</Typography>
				<Typography color="text.secondary" sx={{ maxWidth: 640 }}>
					Update critical metadata, capacity, and availability signals. Changes go live immediately across the scheduler.
				</Typography>
			</Box>

			{error && (
				<Alert severity="error">{error}</Alert>
			)}
			{successMessage && (
				<Alert severity="success">{successMessage}</Alert>
			)}
			{loading && (
				<Stack direction="row" spacing={2} alignItems="center">
					<CircularProgress size={22} />
					<Typography color="text.secondary">Loading rage room details...</Typography>
				</Stack>
			)}

			<Card component="form" onSubmit={handleSubmit}>
				<CardContent>
					<Grid container spacing={3}>
						<Grid item xs={12} md={6}>
							<TextField
								label="Room Name"
								value={formValues.name}
								onChange={handleFieldChange('name')}
								fullWidth
								required
								disabled={loading || submitting}
							/>
						</Grid>
						<Grid item xs={12} md={6}>
							<TextField
								label="City"
								value={formValues.city}
								onChange={handleFieldChange('city')}
								fullWidth
								required
								disabled={loading || submitting}
							/>
						</Grid>
						<Grid item xs={12} md={6}>
							<TextField
								label="Location Name"
								value={formValues.locationName}
								onChange={handleFieldChange('locationName')}
								fullWidth
								required
								disabled={loading || submitting}
							/>
						</Grid>
						<Grid item xs={12}>
							<TextField
								label="Location Address"
								value={formValues.locationAddress}
								onChange={handleFieldChange('locationAddress')}
								fullWidth
								required
								disabled={loading || submitting}
								helperText="Street address or venue details shown to guests."
							/>
						</Grid>
						<Grid item xs={12}>
							<TextField
								label="Description"
								value={formValues.description}
								onChange={handleFieldChange('description')}
								fullWidth
								multiline
								minRows={3}
								disabled={loading || submitting}
							/>
						</Grid>
						<Grid item xs={12} md={6}>
							<TextField
								label="Capacity"
								value={formValues.capacity}
								onChange={handleFieldChange('capacity')}
								fullWidth
								type="number"
								inputProps={{ min: 1 }}
								disabled={loading || submitting}
								helperText="Total guests this room can support per standard session."
							/>
						</Grid>
						<Grid item xs={12} md={6}>
							<TextField
								label="Amenities"
								value={formValues.amenities}
								onChange={handleFieldChange('amenities')}
								fullWidth
								disabled={loading || submitting}
								helperText="Comma-separated list (e.g., Neon Bats, Fog Machine)."
							/>
						</Grid>
						<Grid item xs={12}>
							<FormControlLabel
								control={
									<Switch
										color="primary"
										checked={formValues.isActive}
										onChange={handleIsActiveChange}
										disabled={loading || submitting}
									/>
								}
								label="Room is active and bookable"
							/>
						</Grid>
					</Grid>

					<Divider sx={{ my: 3 }} />

					<Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="flex-end">
						<Button variant="text" color="inherit" onClick={handleCancel} disabled={submitting}>
							Cancel
						</Button>
						<Button type="submit" variant="contained" color="primary" disabled={isSubmitDisabled}>
							{submitting ? 'Saving changes...' : 'Save Changes'}
						</Button>
					</Stack>
				</CardContent>
			</Card>
		</Stack>
	);
};

export default EditRageRoom;